import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProductCard from '../../../miniapp/components/ProductCard';
import type { Product } from '../../../miniapp/types';
import {
  getLocalizedEntityField,
  getNormalizedLanguageCode,
} from '../../../miniapp/i18n/localization';
import { useWebsiteAppContext } from '../../hooks/useWebsiteAppContext';
import { formatCurrency } from '../../utils/formatters';
import { LoadingState } from '../../../miniapp/ui-kit/Spinner';

export default function ManufacturerScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const languageCode = getNormalizedLanguageCode(i18n.resolvedLanguage ?? i18n.language);
  const { manufacturerId } = useParams();
  const { catalogLoading, categories, manufacturers, products, addToCart, removeFromCart, cartItems } =
    useWebsiteAppContext();

  const parsedManufacturerId = Number.parseInt(manufacturerId ?? '', 10);
  const isValidId = Number.isInteger(parsedManufacturerId);
  const selectedManufacturer = useMemo(
    () => (isValidId ? manufacturers.find((item) => item.id === parsedManufacturerId) ?? null : null),
    [isValidId, manufacturers, parsedManufacturerId],
  );

  const selectedManufacturerProducts = useMemo(
    () =>
      isValidId ? products.filter((product) => product.manufacturerId === parsedManufacturerId) : [],
    [isValidId, parsedManufacturerId, products],
  );

  const groupedProducts = useMemo(() => {
    const categoryOrder = new Map<number, number>();
    categories.forEach((category, index) => {
      categoryOrder.set(category.id, index);
    });

    const groups = new Map<
      string,
      { key: string; title: string; order: number; products: Product[] }
    >();

    for (const product of selectedManufacturerProducts) {
      const key = product.categoryId === null ? 'uncategorized' : String(product.categoryId);
      const title =
        product.categoryId === null
          ? t('manufacturer.otherTeas')
          : (() => {
              const matchedCategory = categories.find((category) => category.id === product.categoryId);
              if (matchedCategory) {
                return getLocalizedEntityField(matchedCategory, 'name', languageCode);
              }
              if (product.category) {
                return getLocalizedEntityField(product.category, 'name', languageCode);
              }
              return t('manufacturer.otherTeas');
            })();
      const order =
        product.categoryId === null
          ? Number.MAX_SAFE_INTEGER
          : categoryOrder.get(product.categoryId) ?? Number.MAX_SAFE_INTEGER - 1;

      const existingGroup = groups.get(key);
      if (existingGroup) {
        existingGroup.products.push(product);
      } else {
        groups.set(key, {
          key,
          title,
          order,
          products: [product],
        });
      }
    }

    return Array.from(groups.values()).sort((left, right) => {
      if (left.order !== right.order) {
        return left.order - right.order;
      }
      return left.title.localeCompare(right.title);
    });
  }, [categories, languageCode, selectedManufacturerProducts, t]);

  const cartQuantityByProductKey = useMemo(() => {
    const quantityByKey = new Map<string, number>();
    cartItems.forEach((item) => {
      quantityByKey.set(
        `${item.product.id}-${item.weight}`,
        (quantityByKey.get(`${item.product.id}-${item.weight}`) ?? 0) + item.quantity,
      );
    });
    return quantityByKey;
  }, [cartItems]);

  if (catalogLoading) {
    return (
      <LoadingState
        indicator="pulse"
        label={t('app.loadingProducts')}
        className="min-h-[320px] py-0"
      />
    );
  }

  if (!isValidId || !selectedManufacturer) {
    return (
      <div className="py-20 text-center">
        <h2 className="m-0 text-2xl font-bold text-text">{t('website.manufacturer.notFound')}</h2>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mt-4 bg-accent text-on-accent px-6 py-2.5 rounded-full border-none cursor-pointer"
        >
          {t('success.backToCatalog')}
        </button>
      </div>
    );
  }

  const manufacturerName = getLocalizedEntityField(selectedManufacturer, 'name', languageCode);
  const manufacturerDescription =
    getLocalizedEntityField(selectedManufacturer, 'description', languageCode) ||
    t('manufacturer.defaultDescription');
  const manufacturerImageUrl = selectedManufacturer.image?.url ?? selectedManufacturer.imageUrl ?? null;

  return (
    <section className="grid gap-12">
      <header className="relative rounded-xl overflow-hidden min-h-[280px] md:min-h-[360px] flex items-end bg-secondary shadow-ambient">
        {manufacturerImageUrl ? (
          <img src={manufacturerImageUrl} alt={manufacturerName} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-text-secondary">
            {t('common.noImage')}
          </div>
        )}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,var(--color-hero-overlay-top)_0%,var(--color-hero-overlay-bottom)_100%)]" />
        <div className="relative z-10 max-w-3xl px-6 py-7 md:px-10 md:py-9 text-on-accent">
          <h1 className="m-0 text-4xl md:text-5xl font-bold tracking-tight editorial-title">
            {manufacturerName}
          </h1>
          <p className="m-0 mt-4 leading-relaxed text-[var(--color-hero-chip-text)]">
            {manufacturerDescription}
          </p>
          <span className="inline-block mt-4 text-[0.75rem] font-bold uppercase tracking-[0.04em] text-[var(--color-hero-chip-text)]">
            {t('common.productsCount', { count: selectedManufacturerProducts.length })}
          </span>
        </div>
      </header>

      <div className="grid gap-14">
        {groupedProducts.map((group) => (
          <section key={group.key} className="grid gap-8">
            <div className="flex items-baseline gap-4">
              <h2 className="m-0 text-2xl font-semibold text-text">{group.title}</h2>
              <div className="h-px flex-1 bg-border" />
              <span className="text-[0.6875rem] font-bold uppercase tracking-[0.04em] text-text-secondary">
                {t('common.productCount', { count: group.products.length })}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" aria-live="polite">
              {group.products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  getCartQuantity={(weight) => cartQuantityByProductKey.get(`${product.id}-${weight}`) ?? 0}
                  onOpen={() => navigate(`/product/${product.id}`)}
                  onAddToCart={(weight) => addToCart(product.id, weight)}
                  onRemoveFromCart={(weight) => removeFromCart(product.id, weight)}
                  onOpenManufacturer={(id) => navigate(`/manufacturer/${id}`)}
                  formatCurrency={formatCurrency}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {groupedProducts.length === 0 ? (
        <p className="m-0 text-text-secondary">{t('manufacturer.noProducts')}</p>
      ) : null}
    </section>
  );
}
